/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useIntl } from 'react-intl'

import type { EuiSelectableProps } from '@elastic/eui'
import { EuiFilterButton, EuiPopover, EuiSelectable, useGeneratedHtmlId } from '@elastic/eui'

import type { ResourceType } from '@modules/network-security-lib/types'
import { FormattedMessage } from '@modules/i18n'

const RESOURCE_TYPES: ResourceType[] = ['hosted', 'serverless']

const ResourceTypeFilter: React.FunctionComponent = () => {
  const { formatMessage } = useIntl()

  const htmlId = useGeneratedHtmlId()
  const [isOpen, setIsOpen] = useState(false)

  const [params, setParams] = useSearchParams()
  const selectedResourceType = params.get('resources') || ''

  const labels: Record<ResourceType, string> = {
    hosted: formatMessage({ id: 'networkSecurity.resourceType.hosted', defaultMessage: 'Hosted' }),
    serverless: formatMessage({
      id: 'networkSecurity.resourceType.serverless',
      defaultMessage: 'Serverless',
    }),
  }

  const options: EuiSelectableProps['options'] = RESOURCE_TYPES.map((resourceType) => ({
    key: resourceType,
    label: labels[resourceType],
    checked: selectedResourceType === resourceType ? 'on' : undefined,
  }))

  const button = (
    <EuiFilterButton
      iconType='arrowDown'
      badgeColor='success'
      onClick={() => setIsOpen(!isOpen)}
      isSelected={isOpen}
      hasActiveFilters={Boolean(selectedResourceType)}
    >
      <FormattedMessage id='networkSecurity.resourceTypeFilter.label' defaultMessage='Resources' />
    </EuiFilterButton>
  )

  const onChange: EuiSelectableProps['onChange'] = (newOptions) => {
    const resources = newOptions.filter((item) => item.checked === 'on')[0]?.key || ''

    setParams(
      (params) => {
        const newParams = new URLSearchParams(params)
        newParams.set('resources', resources)
        return newParams
      },
      {
        replace: true,
      },
    )

    setIsOpen(false) // single selection so no need to stick around
  }

  return (
    <EuiPopover
      id={htmlId}
      button={button}
      isOpen={isOpen}
      closePopover={() => setIsOpen(false)}
      panelPaddingSize='none'
    >
      <EuiSelectable
        aria-label='Resource type'
        options={options}
        onChange={onChange}
        listProps={{ isVirtualized: false }}
        singleSelection={true}
      >
        {(list) => <div style={{ width: '12rem' }}>{list}</div>}
      </EuiSelectable>
    </EuiPopover>
  )
}

export default ResourceTypeFilter
